
import * as DTOS from "../FeralScan.dtos";

export function FlattenForGraph(groupedData: DTOS.GroupedData) {
    return groupedData ? groupedData.keyList.map(dataKey => (
        {
            name: dataKey.label,
            data: groupedData.data.map(
                dataPoint =>
                    [
                        dataPoint.label,
                        dataPoint.keyValues.find(d => d.key == dataKey.key).value
                    ]
            )
        }
    )) : undefined;
};

// Takes in an array of numbers, used for e.g. showing continuous spans of year
// [2000,2001,2003,2004,2005,2012,2013,2014,2015] => "2000, 2001, 2003-2005, 2012-2015"
export function ContinuousSpanStringBuilder(data: number[]): string {

    data = data.sort();

    var result: string = '';

    if (data && data.length > 0) {
        // replace consecutive years with a '-'
        result = data.map((x, i) =>
            i !== 0 && x - 1 === data[i - 1] && x + 1 === data[i + 1] ? "-" : '' + x)
            // add commas after everything that isn't part of a consecutive year
            .reduce((a, b) => a + (b !== "-" ? ', ' : '') + b)
            // replace multiple '-' with a single '-'
            .replace(/-+/g, '-')
            .replace(/-, +/g, '-');

        // remove trailing comma if it exists
        if (result[result.length - 2] === ",") {
            result = result.slice(0, -2);
        }
    }

    return result;
}

export enum UserSortBy {
    NAME = 'NAME',
    SURNAME = 'SURNAME',
    APCCODE = 'APCCODE',
    ID = 'ID',
    CREATEDDATE = 'CREATEDDATE',
    APCCODE_CREATEDDATE = 'APCCODE_CREATEDDATE',
    EMAIL = 'EMAIL',
    POSTCODE = 'POSTCODE',
    LASTACTIVITY = 'LASTACTIVITY',
    LASTSCAN = 'LASTSCAN',
    PROBATIONARY = 'PROBATIONARY',
}

export enum PlotSortBy {
    DATESELECTED = 'DATESELECTED',
    MYSCANSCOUNT = 'MYSCANSCOUNT',
    LASTSCANNED = 'LASTSCANNED',
    NAME = 'NAME',
    APCCODE = 'APCCODE',
    WATERBODY = 'WATERBODY',
    NEARESTSUBURB = 'NEARESTSUBURB',
    ID = 'ID',
    CREATEDDATE = 'CREATEDDATE',
    APCCODE_CREATEDDATE = 'APCCODE_CREATEDDATE',
    STATUS = 'STATUS',
}

export enum PlotEventSortBy {
    CREATEDDATE = 'CREATEDDATE',
    EVENTSTARTDATE = 'EVENTSTARTDATE',
}

export enum FileSortBy {
    ID = 'ID',
    CREATEDDATE = 'CREATEDDATE',
    CREATEDBY = 'CREATEDBY',
}

export enum TemplateSortBy {
    ID = 'ID',
    NAME = 'NAME',
    LAYOUTNAME = 'LAYOUTNAME',
}

export const enum SortByDirection {
    ASC = "ASC",
    DESC = "DESC"
}

export const isSortDirection = (x: any): x is SortByDirection => SortByDirection.ASC === x || SortByDirection.DESC === x;

export function sortPlots(plots: DTOS.Plot[], sortBy: PlotSortBy, sortDirection: SortByDirection) {

    //var sortedPlots = plots;
    var sortedPlots: DTOS.Plot[] = plots.slice();
    const directionOffset = sortDirection === "ASC" ? 1 : -1;

    switch (sortBy) {
        case PlotSortBy.ID: {
            sortedPlots.sort((a, b) => {
                return sortNumber(a.id, b.id) * directionOffset;
            });
            break;
        }
        case PlotSortBy.DATESELECTED: {
            sortedPlots.sort((a, b) => {
                return sortNumber(a.userOrderMonitored, b.userOrderMonitored) * directionOffset;
            });
            break;
        }
        case PlotSortBy.CREATEDDATE: {
            sortedPlots.sort((a, b) => {
                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
        case PlotSortBy.LASTSCANNED: {
            sortedPlots.sort((a, b) => {
                return sortDate(a.userNewestPlotEventDate, b.userNewestPlotEventDate) * directionOffset;
            });
            break;
        }
        case PlotSortBy.NAME: {
            sortedPlots.sort((a, b) => {
                return sortAlphabetical(a.name, b.name) * directionOffset;
            });
            break;
        }
        case PlotSortBy.APCCODE: {
            sortedPlots.sort((a, b) => {
                var first = a.apcCode ? a.apcCode.toLowerCase() : undefined;
                var second = b.apcCode ? b.apcCode.toLowerCase() : undefined;

                if (!first && second)
                    return 1
                if (first && !second)
                    return -1

                return sortAlphabetical(a.apcCode, b.apcCode) * directionOffset;
            });
            break;
        }
        case PlotSortBy.WATERBODY: {
            sortedPlots.sort((a, b) => {
                return sortAlphabetical(a.waterBody, b.waterBody) * directionOffset;
            });
            break;
        }
        case PlotSortBy.NEARESTSUBURB: {
            sortedPlots.sort((a, b) => {
                return sortAlphabetical(a.nearestSuburb, b.nearestSuburb) * directionOffset;
            });
            break;
        }
        case PlotSortBy.STATUS: {
            sortedPlots.sort((a, b) => {
                return sortNumber(
                    a.plotStatus ? a.plotStatus.id : Number.MAX_SAFE_INTEGER
                    , b.plotStatus ? b.plotStatus.id : Number.MAX_SAFE_INTEGER
                ) * directionOffset;
            });
            break;
        }
        case PlotSortBy.MYSCANSCOUNT: {
            sortedPlots.sort((a, b) => {
                return sortNumber(a.userPlotEventsCount, b.userPlotEventsCount) * directionOffset;
            });
            break;
        }
        case PlotSortBy.APCCODE_CREATEDDATE: {
            sortedPlots.sort((a, b) => {
                var first = a.apcCode ? a.apcCode.toLowerCase() : undefined;
                var second = b.apcCode ? b.apcCode.toLowerCase() : undefined;

                if ((!first && second) || first < second)
                    return -1
                if ((first && !second) || first > second)
                    return 1

                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
    }

    return sortedPlots;
}

export function sortPlotEvents(plotevents: DTOS.PlotEvent[], sortBy: PlotEventSortBy, sortDirection: SortByDirection) {

    //var sortedPlots = plots;
    var sortedPlotEvents: DTOS.PlotEvent[] = plotevents.slice();
    const directionOffset = sortDirection === "ASC" ? 1 : -1;

    switch (sortBy) {
        case PlotEventSortBy.CREATEDDATE: {
            sortedPlotEvents.sort((a, b) => {
                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
        case PlotEventSortBy.EVENTSTARTDATE: {
            sortedPlotEvents.sort((a, b) => {
                return sortDate(a.eventStartDate, b.eventStartDate) * directionOffset;
            });
            break;
        }
    }

    return sortedPlotEvents;
}
export function sortUsers(users: DTOS.User[], sortBy: UserSortBy, sortDirection: SortByDirection) {

    var sortedUsers: DTOS.User[] = users.slice();
    const directionOffset = sortDirection === "ASC" ? 1 : -1;

    switch (sortBy) {
        case UserSortBy.ID: {
            sortedUsers.sort((a, b) => {
                return sortNumber(a.id, b.id) * directionOffset;
            });
            break;
        }
        case UserSortBy.APCCODE: {
            sortedUsers.sort((a, b) => {
                var first = a.apcCode ? a.apcCode.toLowerCase() : undefined;
                var second = b.apcCode ? b.apcCode.toLowerCase() : undefined;

                if (!first && second)
                    return 1
                if (first && !second)
                    return -1

                return sortAlphabetical(a.apcCode, b.apcCode) * directionOffset;
            });
            break;
        }
        case UserSortBy.CREATEDDATE: {
            sortedUsers.sort((a, b) => {
                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
        case UserSortBy.NAME: {
            sortedUsers.sort((a, b) => {
                return sortAlphabetical(a.fullName, b.fullName) * directionOffset;
            });
            break;
        }
        case UserSortBy.SURNAME: {
            sortedUsers.sort((a, b) => {
                return sortAlphabeticalWithNulls(a.fullName, b.fullName, a.surname, b.surname) * directionOffset;
            });
            break;
        }
        case UserSortBy.EMAIL: {
            sortedUsers.sort((a, b) => {
                return sortAlphabetical(a.email, b.email) * directionOffset;
            });
            break;
        }
        case UserSortBy.LASTACTIVITY: {
            sortedUsers.sort((a, b) => {
                return sortDate(a.lastActivityDate, b.lastActivityDate) * directionOffset;
            });
            break;
        }
        case UserSortBy.POSTCODE: {
            sortedUsers.sort((a, b) => {
                return sortDate(a.postcode, b.postcode) * directionOffset;
            });
            break;
        }
        case UserSortBy.APCCODE_CREATEDDATE: {
            sortedUsers.sort((a, b) => {
                var first = a.apcCode ? a.apcCode.toLowerCase() : undefined;
                var second = b.apcCode ? b.apcCode.toLowerCase() : undefined;

                if ((!first && second) || first < second)
                    return -1
                if ((first && !second) || first > second)
                    return 1

                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
        case UserSortBy.PROBATIONARY: {
            sortedUsers.sort((a, b) => {
                var result = 0;
                
                if (!a.probationary && b.probationary)
                    result = 1
                if (a.probationary && !b.probationary)
                    result = -1

                return result * directionOffset;
            });
            break;
        }
        case UserSortBy.LASTSCAN: {
            sortedUsers.sort((a, b) => {
                return sortDate(a.lastScanDate, b.lastScanDate) * directionOffset;
            });
            break;
        }
    }
    return sortedUsers;
}

export function sortFiles(files: DTOS.FileRecord[], sortBy: FileSortBy, sortDirection: SortByDirection) {

    var sortedFiles: DTOS.FileRecord[] = files.slice();
    const directionOffset = sortDirection === "ASC" ? 1 : -1;

    switch (sortBy) {
        case FileSortBy.ID: {
            sortedFiles.sort((a, b) => {
                return sortNumber(a.id, b.id) * directionOffset;
            });
            break;
        }
        case FileSortBy.CREATEDBY: {
            sortedFiles.sort((a, b) => {
                return sortNumber(a.createdByUserId, b.createdByUserId) * directionOffset;
            });
            break;
        }
        case FileSortBy.CREATEDDATE: {
            sortedFiles.sort((a, b) => {
                return sortDate(a.createdDate, b.createdDate) * directionOffset;
            });
            break;
        }
    }
    return sortedFiles;
}

export function sortTemplates(templates: DTOS.Template[], sortBy: TemplateSortBy, sortDirection: SortByDirection) {

    var sortedTemplates: DTOS.Template[] = templates.slice();
    const directionOffset = sortDirection === "ASC" ? 1 : -1;

    switch (sortBy) {
        case TemplateSortBy.ID: {
            sortedTemplates.sort((a, b) => {
                return sortNumber(a.id, b.id) * directionOffset;
            });
            break;
        }
        case TemplateSortBy.NAME: {
            sortedTemplates.sort((a, b) => {
                return sortAlphabetical(a.name, b.name) * directionOffset;
            });
            break;
        }
        case TemplateSortBy.LAYOUTNAME: {
            sortedTemplates.sort((a, b) => {
                return sortAlphabetical(a.layoutName, b.layoutName) * directionOffset;
            });
            break;
        }
    }
    return sortedTemplates;
}

function sortDate(d1, d2): number {
    if (d1 < d2 || (!d1 && d2))
        return -1;
    if (d1 > d2 || (!d2 && d1))
        return 1;
    return 0;
}

function sortNumber(a: number, b: number): number {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}

function sortAlphabetical(s1: string, s2: string): number {
    var first = s1 ? s1.toLowerCase() : undefined;
    var second = s2 ? s2.toLowerCase() : undefined;

    if (first < second)
        return -1;
    if (first > second)
        return 1;

    return 0;
}

function sortAlphabeticalWithNulls(n1: string, n2: string, s1?: string, s2?: string) {
    var first = s1 ? s1.toLowerCase() : null;
    var second = s2 ? s2.toLowerCase() : null;

    if (first == null && second != null)
        return 1
    if (first != null && second == null)
        return -1
    if (first == null && second == null)
        return sortAlphabetical(n1, n2);

    if (first < second)
        return -1;
    if (first > second)
        return 1;

    return 0;
}